import { FC, useContext, useEffect } from "react";

import { AccordionContainer, Accordion, LoadingSpinner } from "@app/shared";
import { ReportTile } from "./ReportTile";
import { QESButton } from "@app/shared/qes-standards";
import { ExportTypeConverter, ReportsUIModel } from "./reportsModel";
import { ReportsTileCollapseContext, ReportsTileCollapseProvider } from "./ReportsTileCollapseContext";
import { UserLoginStatisticsReportTile } from "./UserLoginStatisticsReportTile";

export interface ReportsPanelProps {
  defaultOpen?: boolean;
  reports: ReportsUIModel[];
  hasLoaded?: boolean;
  onOpen?: () => Promise<void>;
  title: string;
  refreshReports: () => void;
  isYourReports: boolean;
}

const ReportsPanel: FC<React.PropsWithChildren<ReportsPanelProps>> = (props: ReportsPanelProps) => {
  const { collapseAll, expandAll, expandedTiles } = useContext(ReportsTileCollapseContext);

  useEffect(() => {
    if (props.defaultOpen && props.onOpen && !props.hasLoaded) {
      handleOpen();
    }
  }, []);

  const handleOpen = () => {
    if (props.onOpen && !props.hasLoaded) {
      props.onOpen();
    }
  }

  return (
    <div className="row">
      <AccordionContainer className="mt-3 col-12">
        <Accordion
          onPanelWillOpen={handleOpen}
          title={<>{props.title} ({props.reports.length})</>}
          defaultOpen={!!props.defaultOpen}
          testId={`reports-panel__${props.title}-accordion`}
        >
          {!props.hasLoaded && <LoadingSpinner />}
          {props.hasLoaded && props.reports.length === 0 && <h3 className="text-muted">No reports were found.</h3>}
          {props.hasLoaded && props.reports.length > 0 && (
            <>
              {props.reports.length > 0 && (
                <div className="clearfix">
                  <span className="pull-right">
                    <QESButton qesType="secondary" qesTestId={`reports-panel__expand--${props.title}`} onClick={() => expandAll(props.reports.map((d) => d.id))} disabled={expandedTiles.length === props.reports.length}>Expand All</QESButton>
                    <QESButton qesType="secondary" qesTestId={`reports-panel__collapse--${props.title}`} onClick={collapseAll} disabled={expandedTiles.length === 0}>Collapse All</QESButton>
                  </span>
                </div>
              )}
              <div className="row align-items-start">
                {props.reports.map((dl, index) =>
                  dl.exportType === ExportTypeConverter["userLoginStatistics"] ?
                    (
                      <UserLoginStatisticsReportTile key={index} report={dl} />
                    )
                    :
                    (
                      <ReportTile key={index} report={dl} refreshReports={props.refreshReports} isYourReports={props.isYourReports} />
                    ))}
              </div>
            </>
          )}
        </Accordion>
      </AccordionContainer>
    </div >
  );
}

const WrappedReportsPanel: FC<React.PropsWithChildren<ReportsPanelProps>> = (props: ReportsPanelProps) => (
  <ReportsTileCollapseProvider>
    <ReportsPanel {...props} />
  </ReportsTileCollapseProvider>
);

export { WrappedReportsPanel as ReportsPanel };